import { deepCopy } from "@/utils";
import { normalizeR6ProfileData } from "./cyberboardR6Upload";
import { getR6ConnectionErrorMessage } from "./cyberboardR6ErrorMessage";

export default {
  created() {
    // R6 页面使用自己的 CDC bridge，避免复用旧 Qt bridge。
    this.$store.dispatch("loader/setBridge", null);
  },

  data() {
    return {
      r6Bridge: null,
      connectionState: "disconnected",
      isHydrating: false,
      lastSyncedData: null,
      dataSyncChain: Promise.resolve(),
      pendingFullProfileData: null,
      pendingFullProfileDownloadData: null,
      pendingKeySyncData: null,
      pendingKeySyncScope: undefined,
      activeKeySyncData: null,
      keySyncTimer: null,
      keySyncActive: false,
      keySyncNeedsReconcile: false,
      keySyncStatus: "",
      syncStatus: "",
      isSyncing: false,
      syncProgress: {
        current: 0,
        total: 0,
        phase: "preparing",
        profile: false,
        elapsedMs: 0,
        timeoutMs: 0,
      },
    };
  },

  computed: {
    syncProgressPercent() {
      // AM Master reserves 100% for the final 01 06 success response.
      if (this.syncProgress.phase === "complete") return 100;
      if (this.syncProgress.phase === "waiting") return 99;
      if (!this.syncProgress.total) return 0;
      return Math.min(
        99,
        Math.floor((this.syncProgress.current / this.syncProgress.total) * 100)
      );
    },
    syncProgressLabel() {
      const keyPrefix = this.syncProgress.profile ? "profileSync" : "sync";
      if (this.syncProgress.phase === "waiting") {
        return this.$t(`cyberboardR6.${keyPrefix}Waiting`, [
          Math.ceil((this.syncProgress.elapsedMs || 0) / 1000),
        ]);
      }
      if (this.syncProgress.phase === "preparing") {
        return this.$t(`cyberboardR6.${keyPrefix}Preparing`);
      }
      return this.$t(`cyberboardR6.${keyPrefix}Progress`, [
        this.syncProgress.current,
        this.syncProgress.total,
      ]);
    },
  },

  beforeDestroy() {
    clearTimeout(this.keySyncTimer);
    this.keySyncTimer = null;
    if (this.pendingKeySyncData && this.connectionState === "connected") {
      this.flushKeySyncNow();
    }
    if (this.$store.state.loader.bridge === this.r6Bridge) {
      this.$store.dispatch("loader/setBridge", null);
    }
  },

  methods: {
    onCdcReady(bridge) {
      this.r6Bridge = bridge;
      this.syncStatus = "";
      this.lastSyncedData = deepCopy(
        normalizeR6ProfileData(bridge.currentData || this.profileNew.data)
      );
      this.$store.dispatch("loader/setBridge", bridge);
    },

    onCdcState(state) {
      this.connectionState = state;
      if (state !== "connected") {
        if (this.keySyncActive || this.pendingKeySyncData) {
          clearTimeout(this.keySyncTimer);
          this.keySyncTimer = null;
          this.pendingKeySyncData = deepCopy(
            this.profileNew?.data ||
              this.activeKeySyncData ||
              this.pendingKeySyncData
          );
          this.keySyncStatus = "keySyncUncertain";
        }
        return;
      }
      if (state === "connected") {
        this.pendingFullProfileData = null;
        this.pendingFullProfileDownloadData = null;
      }
    },

    onCdcData(data, baselineData, syncInfo) {
      if (!data) return;
      this.isHydrating = true;
      const normalizedData = normalizeR6ProfileData(data);
      const pendingKeyData =
        this.keySyncStatus === "keySyncUncertain"
          ? this.pendingKeySyncData || this.activeKeySyncData
          : null;
      const keyLayerReadbackSucceeded =
        syncInfo?.readbackFeatures?.keyLayer?.success === true;
      if (pendingKeyData && keyLayerReadbackSucceeded) {
        const keyLayerMatches =
          JSON.stringify(normalizedData.key_layer) ===
          JSON.stringify(pendingKeyData.key_layer);
        if (keyLayerMatches) {
          this.pendingKeySyncData = null;
          this.pendingKeySyncScope = undefined;
          this.activeKeySyncData = null;
          this.keySyncStatus = "keySyncSynced";
        } else {
          const retryData = deepCopy(normalizedData);
          retryData.key_layer = deepCopy(pendingKeyData.key_layer);
          const retryScope = ["keymap"];
          if (
            JSON.stringify(normalizedData.tab_key) !==
            JSON.stringify(pendingKeyData.tab_key)
          ) {
            retryData.tab_key = deepCopy(pendingKeyData.tab_key);
            retryScope.push("pressHold");
          }
          this.pendingKeySyncData = retryData;
          this.pendingKeySyncScope = retryScope;
          this.activeKeySyncData = null;
          this.keySyncNeedsReconcile = true;
          this.keySyncStatus = "keySyncing";
          normalizedData.key_layer = deepCopy(retryData.key_layer);
          normalizedData.tab_key = deepCopy(retryData.tab_key);
        }
      }
      if (!this.pendingFullProfileData) {
        this.$store.dispatch("profileNew/setData", normalizedData);
      }
      this.lastSyncedData = deepCopy(baselineData || normalizedData);
      this.$nextTick(() => {
        this.isHydrating = false;
        if (
          this.keySyncNeedsReconcile &&
          this.connectionState === "connected"
        ) {
          this.keySyncNeedsReconcile = false;
          this.flushKeySyncNow();
        }
      });
    },

    enqueueFullProfileSync(data, downloadData = data) {
      const importedData = deepCopy(data);
      const profileDownloadData = deepCopy(downloadData);
      this.isSyncing = true;
      this.syncProgress = {
        current: 0,
        total: 0,
        phase: "preparing",
        profile: true,
        elapsedMs: 0,
        timeoutMs: 0,
      };
      this.dataSyncChain = this.dataSyncChain
        .then(() =>
          this.syncProfileData(
            importedData,
            true,
            undefined,
            (current, total, phase, meta) => {
              this.syncProgress = {
                current,
                total,
                phase,
                profile: true,
                ...(meta || {}),
              };
            },
            undefined,
            undefined,
            profileDownloadData
          )
        )
        .catch((error) => {
          this.pendingFullProfileData = importedData;
          this.pendingFullProfileDownloadData = profileDownloadData;
          if (this.r6Bridge) {
            this.r6Bridge.log("profile-sync:failed", {
              fullProfileWrite: true,
              error: error.message,
            });
          }
          if (this.r6Bridge?.cdc?.jsonTransactionUncertain) {
            this.syncStatus = "syncUncertain";
          }
          this.onCdcError(error);
        })
        .then(() => {
          this.isSyncing = false;
          this.syncProgress = {
            current: 0,
            total: 0,
            phase: "preparing",
            profile: false,
            elapsedMs: 0,
            timeoutMs: 0,
          };
          if (
            this.connectionState === "connected" &&
            this.pendingKeySyncData &&
            !this.keySyncTimer
          ) {
            this.keySyncTimer = setTimeout(() => {
              this.keySyncTimer = null;
              this.flushKeySyncNow();
            }, 0);
          }
        });
    },

    requestFullProfileSync(data, downloadData = data) {
      this.pendingFullProfileData = deepCopy(data);
      this.pendingFullProfileDownloadData = deepCopy(downloadData);
      if (
        this.connectionState === "connected" &&
        this.r6Bridge &&
        this.lastSyncedData
      ) {
        const importedData = deepCopy(this.pendingFullProfileData);
        const profileDownloadData = deepCopy(
          this.pendingFullProfileDownloadData || importedData
        );
        this.pendingFullProfileData = null;
        this.pendingFullProfileDownloadData = null;
        this.enqueueFullProfileSync(importedData, profileDownloadData);
      }
    },

    enqueueProfileChangeSync(data, featureScope) {
      if (this.pendingFullProfileData) {
        this.pendingFullProfileData = deepCopy(data);
        this.pendingFullProfileDownloadData = deepCopy(data);
        return this.dataSyncChain;
      }
      if (this.syncProgress.profile) {
        // Full import owns the CDC session; defer watcher changes until it
        // finishes so the import is not reinterpreted as a full delta.
        this.pendingKeySyncData = deepCopy(data);
        this.pendingKeySyncScope = featureScope;
        this.keySyncStatus = "keySyncing";
        return this.dataSyncChain;
      }
      if (
        this.connectionState !== "connected" ||
        this.isHydrating ||
        !this.r6Bridge ||
        !this.lastSyncedData
      ) {
        return this.dataSyncChain;
      }

      this.pendingKeySyncData = deepCopy(data);
      this.pendingKeySyncScope = featureScope;
      this.keySyncStatus = "keySyncing";
      // Merge edits within this tick or the active write without delaying it.
      if (this.keySyncActive || this.keySyncTimer) return this.dataSyncChain;
      this.keySyncTimer = setTimeout(() => {
        this.keySyncTimer = null;
        this.flushKeySyncNow();
      }, 0);
      return this.dataSyncChain;
    },

    flushKeySyncNow() {
      if (
        this.keySyncActive ||
        !this.pendingKeySyncData ||
        this.connectionState !== "connected" ||
        this.isHydrating ||
        !this.r6Bridge ||
        !this.lastSyncedData
      ) {
        return this.dataSyncChain;
      }

      const snapshot = deepCopy(this.pendingKeySyncData);
      const featureScope = this.pendingKeySyncScope;
      this.pendingKeySyncData = null;
      this.pendingKeySyncScope = undefined;
      this.activeKeySyncData = snapshot;
      this.keySyncActive = true;
      this.keySyncStatus = "keySyncing";
      this.dataSyncChain = this.dataSyncChain
        .then(async () => {
          try {
            await this.syncProfileData(snapshot, false, featureScope);
            if (!this.pendingKeySyncData) {
              this.keySyncStatus = "keySyncSynced";
            }
          } catch (error) {
            if (error.baselineData) {
              this.lastSyncedData = deepCopy(error.baselineData);
            }
            if (this.connectionState !== "connected") {
              this.pendingKeySyncData = deepCopy(
                this.profileNew?.data || snapshot
              );
              this.keySyncStatus = "keySyncUncertain";
            } else {
              this.keySyncStatus = "keySyncFailed";
              this.onCdcError(error);
            }
          } finally {
            this.keySyncActive = false;
            this.activeKeySyncData = null;
            if (
              this.connectionState === "connected" &&
              this.pendingKeySyncData &&
              !this.keySyncTimer
            ) {
              this.keySyncTimer = setTimeout(() => {
                this.keySyncTimer = null;
                this.flushKeySyncNow();
              }, 0);
            }
          }
        })
        .catch((error) => {
          this.keySyncActive = false;
          this.activeKeySyncData = null;
          this.keySyncStatus = "keySyncFailed";
          this.onCdcError(error);
        });
      return this.dataSyncChain;
    },

    syncToDevice(featureScope, pageIndexScope, pageFeatureScope, prepareSync) {
      if (
        this.connectionState !== "connected" ||
        this.isHydrating ||
        !this.r6Bridge ||
        !this.lastSyncedData
      ) {
        return Promise.resolve(false);
      }
      if (this.isSyncing) return this.dataSyncChain;

      this.isSyncing = true;
      this.syncProgress = {
        current: 0,
        total: 0,
        phase: "preparing",
        elapsedMs: 0,
        timeoutMs: 0,
      };
      this.dataSyncChain = this.dataSyncChain
        .then(async () => {
          await this.$nextTick();
          await new Promise((resolve) =>
            window.requestAnimationFrame(() =>
              window.requestAnimationFrame(resolve)
            )
          );
          if (prepareSync && (await prepareSync()) === false) return;
          const snapshot = deepCopy(this.profileNew.data);
          return this.syncProfileData(
            snapshot,
            false,
            featureScope,
            (current, total, phase, meta) => {
              this.syncProgress = { current, total, phase, ...(meta || {}) };
            },
            pageIndexScope,
            pageFeatureScope
          );
        })
        .catch((error) => {
          if (this.r6Bridge) {
            this.r6Bridge.log("profile-sync:failed", {
              fullProfileWrite: false,
              error: error.message,
            });
          }
          if (this.r6Bridge?.cdc?.jsonTransactionUncertain) {
            this.syncStatus = "syncUncertain";
          }
          this.onCdcError(error);
        })
        .then(() => {
          this.isSyncing = false;
          this.syncProgress = {
            current: 0,
            total: 0,
            phase: "preparing",
            elapsedMs: 0,
            timeoutMs: 0,
          };
        });
      return this.dataSyncChain;
    },

    async syncProfileData(
      newData,
      fullProfileWrite = false,
      featureScope = undefined,
      onProgress = undefined,
      pageIndexScope = undefined,
      pageFeatureScope = undefined,
      downloadData = undefined
    ) {
      if (
        this.connectionState !== "connected" ||
        this.isHydrating ||
        !this.r6Bridge ||
        !this.lastSyncedData
      )
        return;

      let syncResult;
      if (fullProfileWrite) {
        await this.r6Bridge.sendFullProfile(
          downloadData || newData,
          onProgress
        );
        // A partial import uses a wire payload with ignored sections; keep the
        // merged local profile as the bridge snapshot for later edits.
        this.r6Bridge.currentData = deepCopy(newData);
        this.pendingFullProfileData = null;
        this.pendingFullProfileDownloadData = null;
      } else {
        syncResult = await this.r6Bridge.syncProfileChanges(
          newData,
          this.lastSyncedData,
          featureScope,
          onProgress,
          pageIndexScope,
          pageFeatureScope
        );
        if (Object.keys(syncResult.readback).length) {
          const localData = this.profileNew?.data;
          const hasNewerLocalEdit =
            localData && JSON.stringify(localData) !== JSON.stringify(newData);
          if (hasNewerLocalEdit) {
            // Keep the editor on the newest local snapshot while queued writes finish.
            this.lastSyncedData = deepCopy(
              syncResult.baselineData || syncResult.data
            );
          } else {
            this.onCdcData(syncResult.data, syncResult.baselineData);
          }
          return;
        }
      }
      // This baseline tracks accepted changes; features without readback are not device-read state.
      this.lastSyncedData = deepCopy(
        fullProfileWrite ? newData : syncResult.baselineData || newData
      );
    },

    onCdcError(error) {
      console.error("R6 device operation failed", error?.message || error);
      if (this.$message) {
        const connectionMessage = getR6ConnectionErrorMessage(
          error,
          this.$t.bind(this)
        );
        if (connectionMessage) {
          this.$message.error(connectionMessage);
          return;
        }
        const reason = error?.message || String(error || "");
        this.$message.error(
          reason
            ? `${this.$t("cyberboardR6.operationFailed")} (${reason})`
            : this.$t("cyberboardR6.operationFailed")
        );
      }
    },
  },
};
